import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { UI_BODY_BOLD, colors2, transition } from "oolib";
import { ButtonTertiary } from "oolib/dist/v2/components/Buttons";
import { ArrowLeft } from "@phosphor-icons/react";
import { StyledPanelOverlay, StyledBackButton } from "../lightbox/styled";

const SLIDE_MS = 400;

const StyledPanelWrapper = styled.div<{
  $isOpen?: boolean;
  $width: number;
  $zIndex: number;
  $rightOffset: number;
}>`
  position: fixed;
  top: 0;
  right: ${({ $rightOffset }) => $rightOffset}px;
  width: ${({ $width }) => $width}px;
  height: 100vh;
  background: ${colors2.white};
  border-left: 1px solid ${colors2.grey10};
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.08);
  z-index: ${({ $zIndex }) => $zIndex};
  display: flex;
  flex-direction: column;
  transform: ${({ $isOpen }) => ($isOpen ? "translateX(0)" : "translateX(100%)")};
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
  ${transition(
    `transform ${SLIDE_MS}ms cubic-bezier(0.25, 1, 0.4, 1)`,
    `right ${SLIDE_MS}ms cubic-bezier(0.25, 1, 0.4, 1)`
  )}
`;

const StyledPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 14px;
  gap: 12px;
`;

// Between the header and the scrolling body: a count or status line under the
// title that has to stay put while the body scrolls.
const StyledPanelSubHeader = styled.div`
  padding: 0 14px 14px;
  flex-shrink: 0;
`;

const StyledPanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  background: ${colors2.white};
`;

// The covered state (SidePanelStack): this panel sits behind newer panels
// with only a sliver showing. At rest a light haze says "not current";
// hovering brings up the multilayer lightbox's own blur scrim and back
// arrow, centred in the sliver via --panel-offset, and a press anywhere on
// it returns here (closes everything in front). It acts on mousedown, not
// click: the front panel's outside-click close also fires on mousedown,
// re-renders the stack, and would remove this cover before a click landed.
const StyledCover = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  background: rgba(255, 255, 255, 0.45);
  cursor: pointer;

  &:hover ${StyledPanelOverlay} {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover ${StyledBackButton} {
    opacity: 1;
    left: calc(var(--panel-offset) / 2);
    transform: translate(-50%, -50%) scale(1);
    pointer-events: auto;
  }
`;

interface SidePanelProps {
  isOpen?: boolean;
  onClose?: () => void;
  title?: ReactNode;
  children?: ReactNode;
  width?: number;
  zIndex?: number;
  rightOffset?: number;
  bodyStyle?: CSSProperties;
  headerRight?: ReactNode;
  subHeader?: ReactNode;
  closeOnOutsideClick?: boolean;
  /** Behind newer panels in a stack: hazed, hover shows a back arrow. */
  covered?: boolean;
  /** Clicking the covered panel: bring it back to the front. */
  onUncover?: () => void;
  /** How much of a covered panel stays visible, px (the arrow centres in it). */
  peekWidth?: number;
  /** Panels sharing a group are fixed to the same spot: opening one closes the rest. */
  exclusiveGroup?: string;
  /**
   * What the panel is showing. A new key slides the open panel out with its
   * old content and back in with the new, so a swap reads as a new panel.
   */
  contentKey?: string | null;
}

// The close handler of the open panel in each exclusive group.
const openPanelCloser = new Map<string, () => void>();

export const SidePanel = ({
  isOpen,
  onClose,
  title,
  children,
  width = 350,
  zIndex = 1000,
  rightOffset = 0,
  bodyStyle = {},
  headerRight,
  subHeader,
  closeOnOutsideClick = true,
  covered = false,
  onUncover,
  peekWidth = 160,
  exclusiveGroup,
  contentKey,
}: SidePanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // While the key catches up, the panel is shut and keeps its last content, so
  // it leaves showing what it showed. Closing (key -> null) gets the same.
  const [shownKey, setShownKey] = useState(contentKey);
  const swapping = shownKey !== contentKey;
  const lastContent = useRef({ title, subHeader, children });
  if (!swapping) lastContent.current = { title, subHeader, children };
  const view = swapping ? lastContent.current : { title, subHeader, children };
  const shownOpen = !!isOpen && !swapping;

  const wasOpen = useRef(shownOpen);
  const swapDelay = wasOpen.current ? SLIDE_MS : 0;
  useEffect(() => {
    wasOpen.current = shownOpen;
  });
  useEffect(() => {
    if (!swapping) return;
    const timer = setTimeout(() => setShownKey(contentKey), swapDelay);
    return () => clearTimeout(timer);
  }, [swapping, contentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || !exclusiveGroup) return;
    const close = () => onClose?.();
    openPanelCloser.get(exclusiveGroup)?.();
    openPanelCloser.set(exclusiveGroup, close);
    return () => {
      if (openPanelCloser.get(exclusiveGroup) === close) openPanelCloser.delete(exclusiveGroup);
    };
  }, [isOpen, exclusiveGroup, onClose]);

  useEffect(() => {
    if (!isOpen || !closeOnOutsideClick) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose?.();
      }
    };
    // Defer attaching to the next tick so the click that opened the panel
    // doesn't immediately close it.
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleMouseDown);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isOpen, closeOnOutsideClick, onClose]);

  return (
    <StyledPanelWrapper
      ref={panelRef}
      $isOpen={shownOpen}
      $width={width}
      $zIndex={zIndex}
      $rightOffset={rightOffset}
    >
      <StyledPanelHeader>
        {/* Mounted only with a title: oolib's typo localizes (with a hook) only
            string children, so one that goes empty -> string reorders its hooks. */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {view.title && <UI_BODY_BOLD>{view.title}</UI_BODY_BOLD>}
        </div>
        {headerRight}
        <ButtonTertiary
          icon="X"
          onClick={onClose}
          style={{ borderRadius: "1.4rem", padding: "4px 10px" }}
          iconSize={14}
        />
      </StyledPanelHeader>
      {view.subHeader && <StyledPanelSubHeader>{view.subHeader}</StyledPanelSubHeader>}
      <StyledPanelBody style={bodyStyle}>
        {view.children}
      </StyledPanelBody>
      {covered && (
        <StyledCover
          style={{ "--panel-offset": `${peekWidth}px` } as CSSProperties}
          onMouseDown={onUncover}
          role="button"
          aria-label="Back to this panel"
        >
          <StyledPanelOverlay />
          <StyledBackButton type="button">
            <ArrowLeft size={20} color={colors2.grey80} weight="bold" />
          </StyledBackButton>
        </StyledCover>
      )}
    </StyledPanelWrapper>
  );
};
